import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert, AuthCard } from '../layout';

type State = 'pending' | 'approved' | 'declined' | 'used' | 'revoked' | 'expired';
interface ConsentRequest { company: string; reason: string; requestedAt: string; respondBy: string; state: State }

export const STATE_LABELS: Record<State, string> = {
  pending: 'Laukia jūsų sprendimo', approved: 'Leista (galioja 24 val.)', declined: 'Atsisakyta', used: 'Panaudota', revoked: 'Atšaukta', expired: 'Nebegalioja',
};

/**
 * B3a — the page the access e-mail opens: what the administrator asks and why; the owner decides with a
 * button (mail scanners that open links decide nothing).
 */
export function ViewConsentPage({ token }: { token: string }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const lookup = useQuery({ queryKey: ['view-consent', token], enabled: Boolean(token), retry: false,
    queryFn: () => api.post<{ request: ConsentRequest }>('api/v1/view-consent/lookup', { token }).then((r) => r.request) });
  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'decline') => api.post<{ decision: string }>('api/v1/view-consent', { token, decision }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['view-consent', token] }),
  });
  const r = lookup.data;
  return (
    <AuthCard eyebrow="FakturaSmart Pro" title={t('Prašymas peržiūrėti paskyrą')}
      intro={t('Administratorius prašo leidimo vieną valandą peržiūrėti jūsų paskyrą. Tik skaityti — nieko keisti negalės. Prieigą visada galite atšaukti skiltyje Nustatymai → Saugumas.')}
      footer={<Link to="/login">{t('Prisijungti')}</Link>}>
      {lookup.isError ? <Alert kind="error">{errorText(t, lookup.error)}</Alert> : null}
      {decide.isError ? <Alert kind="error">{errorText(t, decide.error)}</Alert> : null}
      {r ? (
        <div className="consent-box">
          <p><span className="muted">{t('Įmonė')}</span><strong>{r.company}</strong></p>
          <p><span className="muted">{t('Priežastis')}</span><strong>{r.reason}</strong></p>
          <p><span className="muted">{t('Paprašyta')}</span>{new Date(r.requestedAt).toLocaleString()}</p>
          <p><span className="muted">{t('Būsena')}</span><strong>{t(STATE_LABELS[r.state])}</strong></p>
        </div>
      ) : null}
      {decide.isSuccess ? <Alert kind="success">{decide.data.decision === 'approve' ? t('Leidimas suteiktas. Administratorius galės vieną kartą peržiūrėti paskyrą per 24 valandas.') : t('Atsisakyta. Administratorius paskyros nematys.')}</Alert> : null}
      {r?.state === 'pending' && !decide.isSuccess ? (
        <div className="form-actions">
          <button type="button" className="button button--primary" disabled={decide.isPending} onClick={() => decide.mutate('approve')}>{t('Leisti peržiūrėti')}</button>
          <button type="button" className="button button--ghost" disabled={decide.isPending} onClick={() => decide.mutate('decline')}>{t('Atsisakyti')}</button>
        </div>
      ) : null}
    </AuthCard>
  );
}
